import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { env } from '@/core/config/env';
import type { PagedResult } from '@/shared/types/pagedResult';
import type {
  CreatedResponse,
  CreateFormPayload,
  CreateTaskFromSubmissionPayload,
  FieldGridCount,
  FieldOptionCount,
  FormAnalytics,
  FormAnalyticsAbandonmentField,
  FormAnalyticsRespondent,
  FormDetail,
  FormSharingDto,
  FormsFilters,
  FormSummary,
  FormVersion,
  PublicFormDetail,
  ShareFormPayload,
  ShareFormResult,
  Submission,
  SubmissionDetail,
  SubmissionsFilters,
  SubmitFormPayload,
  TemplateDetail,
  UpdateFormPayload,
} from '../types';
import { mockFormVersions, toPublicDetail, toSubmission, toSummary } from './forms.mock';
import {
  defaultSharingFor,
  formsMockState,
  formsSharingMockState,
  MOCK_CURRENT_USER_ID,
  submissionsMockState,
  templatesMockState,
} from './forms-mock-state';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

interface EventIdResponse {
  eventId: string;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Mirrors the backend's `GET /forms/{formId}/analytics` aggregation, computed from mock submission values. */
function computeMockAnalytics(formId: string): FormAnalytics {
  const form = formsMockState.find((f) => f.id === formId);
  const submissions = submissionsMockState.filter((s) => s.formId === formId);
  const totalSubmissions = submissions.length;
  const sharing = formsSharingMockState[formId];
  const isSpecific = sharing?.audienceType === 'Specific';

  const totalRecipients = isSpecific ? (sharing?.recipients.length ?? 0) : null;
  const respondedRecipients = isSpecific
    ? (sharing?.recipients.filter((r) => r.respondedAt).length ?? 0)
    : null;
  const responseRate =
    totalRecipients !== null && respondedRecipients !== null
      ? totalRecipients === 0
        ? 0
        : round1((respondedRecipients / totalRecipients) * 100)
      : null;

  const lastSubmissionAt = submissions.length
    ? submissions.reduce(
        (latest, s) => (s.createdAt > latest ? s.createdAt : latest),
        submissions[0].createdAt,
      )
    : null;

  const fields = (form?.fields ?? [])
    .filter((f) => f.id !== null)
    .map((field) => {
      const fieldId = field.id as string;
      const rawValues = submissions.map((s) => s.values.find((v) => v.fieldId === fieldId)?.value);
      const nonEmptyValues = rawValues.filter((v): v is string => !!v?.trim());
      const emptyCount = field.required ? null : totalSubmissions - nonEmptyValues.length;

      let optionCounts: FieldOptionCount[] | null = null;
      let numericStats: { average: number | null; min: number | null; max: number | null } | null =
        null;
      let gridCounts: FieldGridCount[] | null = null;

      if (
        field.fieldType === 'Select' ||
        field.fieldType === 'MultiSelect' ||
        field.fieldType === 'Checkbox'
      ) {
        const counts = new Map<string, number>();
        for (const value of nonEmptyValues) {
          const options =
            field.fieldType === 'MultiSelect' ? value.split(',').map((v) => v.trim()) : [value];
          for (const option of options) counts.set(option, (counts.get(option) ?? 0) + 1);
        }
        const total = nonEmptyValues.length;
        optionCounts = Array.from(counts.entries())
          .map(([option, count]) => ({
            option,
            count,
            percentage: total === 0 ? 0 : round1((count / total) * 100),
          }))
          .sort((a, b) => b.count - a.count);
      } else if (field.fieldType === 'Number' || field.fieldType === 'LinearScale') {
        const numbers = nonEmptyValues.map(Number).filter((n) => !Number.isNaN(n));
        numericStats = numbers.length
          ? {
              average: round1(numbers.reduce((a, b) => a + b, 0) / numbers.length),
              min: Math.min(...numbers),
              max: Math.max(...numbers),
            }
          : { average: null, min: null, max: null };
      } else if (field.fieldType === 'Grid') {
        const counts = new Map<string, number>();
        for (const value of nonEmptyValues) {
          try {
            const parsed = JSON.parse(value) as Record<string, string>;
            for (const [row, column] of Object.entries(parsed)) {
              counts.set(`${row} ${column}`, (counts.get(`${row} ${column}`) ?? 0) + 1);
            }
          } catch {
            // ignore malformed mock values
          }
        }
        const total = nonEmptyValues.length;
        gridCounts = Array.from(counts.entries()).map(([key, count]) => {
          const [row, column] = key.split(' ');
          return {
            row,
            column,
            count,
            percentage: total === 0 ? 0 : round1((count / total) * 100),
          };
        });
      }

      return {
        fieldId,
        label: field.label,
        fieldType: field.fieldType,
        required: field.required,
        totalResponses: nonEmptyValues.length,
        emptyCount,
        optionCounts,
        numericStats,
        gridCounts,
      };
    });

  const abandonmentCandidates = fields.filter(
    (f): f is typeof f & { emptyCount: number } =>
      !f.required && !!f.emptyCount && f.emptyCount > 0,
  );
  const highestAbandonmentField: FormAnalyticsAbandonmentField | null = abandonmentCandidates.length
    ? abandonmentCandidates
        .map((f) => ({
          fieldId: f.fieldId,
          label: f.label,
          emptyCount: f.emptyCount,
          totalSubmissions,
          abandonmentRate:
            totalSubmissions === 0 ? 0 : round1((f.emptyCount / totalSubmissions) * 100),
        }))
        .sort((a, b) => b.emptyCount - a.emptyCount)[0]
    : null;

  const respondents: FormAnalyticsRespondent[] = submissions.map((s) => ({
    userId: s.submittedById,
    email: s.submittedByEmail,
    isAnonymous: s.isAnonymous,
    submissionId: s.id,
    respondedAt: s.createdAt,
  }));

  return {
    totalSubmissions,
    totalRecipients,
    respondedRecipients,
    responseRate,
    lastSubmissionAt,
    highestAbandonmentField,
    fields,
    respondents,
  };
}

/**
 * Wire shape for `POST /forms` — `projectId` travels as a JSON number per the
 * backend contract, unlike the string the rest of the frontend uses for IDs.
 * Snowflake IDs can exceed `Number.MAX_SAFE_INTEGER`; flagged as a known risk.
 */
interface CreateFormRequestBody extends Omit<CreateFormPayload, 'projectId'> {
  projectId: number | null;
}

export const formsService = {
  getList: async (filters: FormsFilters, page: number, pageSize: number, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const filtered = formsMockState.filter((form) => {
        if (filters.status && form.status !== filters.status) return false;
        if (filters.projectId && form.projectId !== filters.projectId) return false;
        if (
          filters.searchTerm &&
          !form.title.toLowerCase().includes(filters.searchTerm.toLowerCase())
        ) {
          return false;
        }
        return true;
      });
      const start = (page - 1) * pageSize;
      const items = filtered.slice(start, start + pageSize).map(toSummary);
      const totalCount = filtered.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      return Promise.resolve({
        data: {
          items,
          page,
          pageSize,
          totalCount,
          totalPages,
          hasPreviousPage: page > 1,
          hasNextPage: page < totalPages,
          firstItemOnPage: totalCount === 0 ? 0 : start + 1,
          lastItemOnPage: Math.min(start + pageSize, totalCount),
        } satisfies PagedResult<FormSummary>,
      });
    }
    return httpClient.get<PagedResult<FormSummary>>(`${API_V1}/forms`, {
      params: {
        Page: page,
        PageSize: pageSize,
        SearchTerm: filters.searchTerm || undefined,
        status: filters.status,
        projectId: filters.projectId,
      },
      signal,
    });
  },

  getById: async (formId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const form = formsMockState.find((f) => f.id === formId);
      if (!form) return Promise.reject(new Error('Form.NotFound'));
      return Promise.resolve({ data: form });
    }
    return httpClient.get<FormDetail>(`${API_V1}/forms/${formId}`, { signal });
  },

  /** Anonymous read — requires the form's sharing `audienceType` to be `Public` (`GET /forms/{id}/sharing`). */
  getPublicById: async (formId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const form = formsMockState.find((f) => f.id === formId);
      if (!form) return Promise.reject(new Error('Form.NotFound'));
      return Promise.resolve({ data: toPublicDetail(form) });
    }
    return httpClient.get<PublicFormDetail>(`${API_V1}/forms/${formId}/public`, { signal });
  },

  getVersions: async (formId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: mockFormVersions[formId] ?? [] });
    }
    return httpClient.get<FormVersion[]>(`${API_V1}/forms/${formId}/versions`, { signal });
  },

  create: async (data: CreateFormPayload) => {
    if (USE_MOCK) {
      const newForm: FormDetail = {
        id: String(Date.now()),
        title: data.title,
        description: data.description ?? null,
        projectId: data.projectId ?? null,
        status: 'Draft',
        // Not settable via Create anymore — defaults until the sharing config is set.
        audienceType: 'Private',
        expiresAt: null,
        emailRequired: data.emailRequired,
        currentVersion: 0,
        fields: data.fields.map((f) => ({
          ...f,
          id: null,
          options: f.options ?? null,
          validation: f.validation ?? null,
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      formsMockState.unshift(newForm);
      formsSharingMockState[newForm.id] = defaultSharingFor(newForm);
      return Promise.resolve({ data: { id: newForm.id } satisfies CreatedResponse });
    }
    return httpClient.post<CreateFormRequestBody, CreatedResponse>(`${API_V1}/forms`, {
      ...data,
      projectId: data.projectId ? Number(data.projectId) : null,
    });
  },

  update: async (formId: string, data: UpdateFormPayload) => {
    if (USE_MOCK) {
      const idx = formsMockState.findIndex((f) => f.id === formId);
      if (idx !== -1) {
        formsMockState[idx] = {
          ...formsMockState[idx],
          title: data.title,
          description: data.description ?? null,
          emailRequired: data.emailRequired,
          fields: data.fields.map((f) => ({
            ...f,
            id: null,
            options: f.options ?? null,
            validation: f.validation ?? null,
          })),
          updatedAt: new Date().toISOString(),
        };
      }
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.put<UpdateFormPayload, void>(`${API_V1}/forms/${formId}`, data);
  },

  /** Creates a copy in Draft status with the same fields. Does not copy sharing config, recipients, or submissions. */
  duplicate: async (formId: string, title?: string) => {
    if (USE_MOCK) {
      const original = formsMockState.find((f) => f.id === formId);
      if (!original) return Promise.reject(new Error('Form.NotFound'));
      const newForm: FormDetail = {
        ...original,
        id: String(Date.now()),
        title: title || `${original.title} (copia)`,
        status: 'Draft',
        audienceType: 'Private',
        expiresAt: null,
        currentVersion: 0,
        fields: original.fields.map((f) => ({ ...f, id: null })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      formsMockState.unshift(newForm);
      formsSharingMockState[newForm.id] = defaultSharingFor(newForm);
      return Promise.resolve({ data: { id: newForm.id } satisfies CreatedResponse });
    }
    return httpClient.post<{ title?: string }, CreatedResponse>(
      `${API_V1}/forms/${formId}/duplicate`,
      {
        title,
      },
    );
  },

  /** Hard delete. Blocked with `Form.HasSubmissions` if the form has submissions — archive instead. */
  delete: async (formId: string) => {
    if (USE_MOCK) {
      const hasSubmissions = submissionsMockState.some((s) => s.formId === formId);
      if (hasSubmissions) return Promise.reject(new Error('Form.HasSubmissions'));
      const idx = formsMockState.findIndex((f) => f.id === formId);
      if (idx === -1) return Promise.reject(new Error('Form.NotFound'));
      formsMockState.splice(idx, 1);
      delete formsSharingMockState[formId];
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/forms/${formId}`);
  },

  /** Creates a `FormTemplate` from this form's current fields. Auto-prefixes with " (plantilla)" if no title is given. */
  saveAsTemplate: async (formId: string, title?: string) => {
    if (USE_MOCK) {
      const form = formsMockState.find((f) => f.id === formId);
      if (!form) return Promise.reject(new Error('Form.NotFound'));
      const newTemplate: TemplateDetail = {
        id: String(Date.now()),
        title: title || `${form.title} (plantilla)`,
        description: form.description,
        emailRequired: form.emailRequired,
        fields: form.fields.map((f) => ({ ...f })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      templatesMockState.unshift(newTemplate);
      return Promise.resolve({ data: { id: newTemplate.id } satisfies CreatedResponse });
    }
    return httpClient.post<{ title?: string }, CreatedResponse>(
      `${API_V1}/forms/${formId}/save-as-template`,
      { title },
    );
  },

  /** Reads the audience/recipients/expiration configuration for a form. */
  getSharing: async (formId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const form = formsMockState.find((f) => f.id === formId);
      const sharing = formsSharingMockState[formId] ?? (form ? defaultSharingFor(form) : undefined);
      if (!sharing) return Promise.reject(new Error('Form.NotFound'));
      return Promise.resolve({ data: sharing });
    }
    return httpClient.get<FormSharingDto>(`${API_V1}/forms/${formId}/sharing`, { signal });
  },

  /**
   * Configures audience/recipients/expiration, creates a version snapshot, and
   * distributes it — a single unified action. Replaces the old two-step
   * `setSharing` + `share` (backend merged them, see forms-backend-requests.md)
   * and the earlier obsolete `publish`.
   */
  share: async (formId: string, data: ShareFormPayload) => {
    if (USE_MOCK) {
      const current = formsSharingMockState[formId];
      const nextSharing: FormSharingDto = {
        audienceType: data.audienceType,
        recipients: data.recipients.map((r) => {
          const existing = current?.recipients.find(
            (rec) => rec.userId === (r.userId ?? null) && rec.email === (r.email ?? null),
          );
          return {
            userId: r.userId ?? null,
            email: r.email ?? null,
            type: r.type,
            token: existing?.token ?? null,
            tokenSentAt: existing?.tokenSentAt ?? null,
            respondedAt: existing?.respondedAt ?? null,
            displayName: existing?.displayName ?? null,
            avatarUrl: existing?.avatarUrl ?? null,
          };
        }),
        expiresAt: data.expiresAt ?? null,
        publicLink: current?.publicLink ?? null,
      };
      const idx = formsMockState.findIndex((f) => f.id === formId);
      if (idx !== -1) {
        formsMockState[idx] = {
          ...formsMockState[idx],
          audienceType: data.audienceType,
          expiresAt: data.expiresAt ?? null,
          status: 'Shared',
          currentVersion: formsMockState[idx].currentVersion + 1,
          fields: formsMockState[idx].fields.map((f, i) => ({ ...f, id: f.id ?? String(i) })),
        };
      }
      const newRecipientCount = nextSharing.recipients.filter(
        (r) => !current?.recipients.some((rec) => rec.userId === r.userId && rec.email === r.email),
      ).length;
      formsSharingMockState[formId] = {
        ...nextSharing,
        recipients: nextSharing.recipients.map((r, i) => ({
          ...r,
          token: r.token ?? `mock-token-${formId}-${i}`,
          tokenSentAt: r.tokenSentAt ?? new Date().toISOString(),
        })),
        publicLink:
          nextSharing.audienceType === 'Public'
            ? (nextSharing.publicLink ?? `${window.location.origin}/f/${formId}`)
            : nextSharing.publicLink,
      };
      return Promise.resolve({
        data: {
          publicLink: formsSharingMockState[formId].publicLink,
          emailsSent: nextSharing.recipients.filter((r) => r.type === 'Email').length,
          inAppNotificationsSent: newRecipientCount,
        } satisfies ShareFormResult,
      });
    }
    return httpClient.post<ShareFormPayload, ShareFormResult>(
      `${API_V1}/forms/${formId}/share`,
      data,
    );
  },

  /** Response analytics: totals, per-field breakdown, abandonment, and respondents. */
  getAnalytics: async (formId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: computeMockAnalytics(formId) });
    }
    return httpClient.get<FormAnalytics>(`${API_V1}/forms/${formId}/analytics`, { signal });
  },

  /** Forms where the current user is a `Specific` recipient. */
  getAssignedToMe: async (page: number, pageSize: number, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const assigned = formsMockState.filter((f) =>
        formsSharingMockState[f.id]?.recipients.some((r) => r.userId === MOCK_CURRENT_USER_ID),
      );
      const start = (page - 1) * pageSize;
      const items = assigned.slice(start, start + pageSize).map(toSummary);
      const totalCount = assigned.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      return Promise.resolve({
        data: {
          items,
          page,
          pageSize,
          totalCount,
          totalPages,
          hasPreviousPage: page > 1,
          hasNextPage: page < totalPages,
          firstItemOnPage: totalCount === 0 ? 0 : start + 1,
          lastItemOnPage: Math.min(start + pageSize, totalCount),
        } satisfies PagedResult<FormSummary>,
      });
    }
    return httpClient.get<PagedResult<FormSummary>>(`${API_V1}/forms/assigned-to-me`, {
      params: { Page: page, PageSize: pageSize },
      signal,
    });
  },

  /** Validates a per-recipient share token and returns the form schema for `Specific`-audience access. */
  getByAccessToken: async (token: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const form = formsMockState.find((f) =>
        formsSharingMockState[f.id]?.recipients.some((r) => r.token === token),
      );
      if (!form) return Promise.reject(new Error('Form.NotFound'));
      return Promise.resolve({ data: toPublicDetail(form) });
    }
    return httpClient.get<PublicFormDetail>(`${API_V1}/forms/access/${token}`, { signal });
  },

  close: async (formId: string) => {
    if (USE_MOCK) {
      const idx = formsMockState.findIndex((f) => f.id === formId);
      if (idx !== -1) formsMockState[idx] = { ...formsMockState[idx], status: 'Closed' };
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.post<undefined, void>(`${API_V1}/forms/${formId}/close`, undefined);
  },

  archive: async (formId: string) => {
    if (USE_MOCK) {
      const idx = formsMockState.findIndex((f) => f.id === formId);
      if (idx !== -1) formsMockState[idx] = { ...formsMockState[idx], status: 'Archived' };
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.post<undefined, void>(`${API_V1}/forms/${formId}/archive`, undefined);
  },

  submit: async (formId: string, data: SubmitFormPayload) => {
    if (USE_MOCK) {
      const newSubmission: SubmissionDetail = {
        id: String(Date.now()),
        formId,
        submittedById: data.isAnonymous ? null : 'current-user',
        submittedByEmail: data.email ?? null,
        isAnonymous: data.isAnonymous,
        taskId: null,
        formVersionId: '1',
        values: data.values,
        createdAt: new Date().toISOString(),
      };
      submissionsMockState.unshift(newSubmission);
      return Promise.resolve({ data: { id: newSubmission.id } satisfies CreatedResponse });
    }
    return httpClient.post<SubmitFormPayload, CreatedResponse>(
      `${API_V1}/forms/${formId}/submit`,
      data,
    );
  },

  getSubmissions: async (
    formId: string,
    filters: SubmissionsFilters,
    page: number,
    pageSize: number,
    signal?: AbortSignal,
  ) => {
    if (USE_MOCK) {
      const filtered = submissionsMockState.filter((s) => {
        if (s.formId !== formId) return false;
        if (filters.anonymous !== undefined && s.isAnonymous !== filters.anonymous) return false;
        if (filters.from && s.createdAt < filters.from) return false;
        if (filters.to && s.createdAt > filters.to) return false;
        return true;
      });
      const start = (page - 1) * pageSize;
      const items = filtered.slice(start, start + pageSize).map(toSubmission);
      const totalCount = filtered.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      return Promise.resolve({
        data: {
          items,
          page,
          pageSize,
          totalCount,
          totalPages,
          hasPreviousPage: page > 1,
          hasNextPage: page < totalPages,
          firstItemOnPage: totalCount === 0 ? 0 : start + 1,
          lastItemOnPage: Math.min(start + pageSize, totalCount),
        } satisfies PagedResult<Submission>,
      });
    }
    return httpClient.get<PagedResult<Submission>>(`${API_V1}/forms/${formId}/submissions`, {
      params: {
        Page: page,
        PageSize: pageSize,
        anonymous: filters.anonymous,
        from: filters.from,
        to: filters.to,
      },
      signal,
    });
  },

  getSubmission: async (formId: string, submissionId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const submission = submissionsMockState.find(
        (s) => s.formId === formId && s.id === submissionId,
      );
      if (!submission) return Promise.reject(new Error('Submission.NotFound'));
      return Promise.resolve({ data: submission });
    }
    return httpClient.get<SubmissionDetail>(
      `${API_V1}/forms/${formId}/submissions/${submissionId}`,
      {
        signal,
      },
    );
  },

  deleteSubmission: async (formId: string, submissionId: string) => {
    if (USE_MOCK) {
      const idx = submissionsMockState.findIndex(
        (s) => s.formId === formId && s.id === submissionId,
      );
      if (idx === -1) return Promise.reject(new Error('Submission.NotFound'));
      submissionsMockState.splice(idx, 1);
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/forms/${formId}/submissions/${submissionId}`);
  },

  /**
   * Used client-side by `useFieldCompletionStats` — capped at 200 submissions, see
   * forms-backend-requests.md P1. `GET /submissions` returns a paged list of
   * `SubmissionDto` (no `values`), so each submission's real detail (with `values`)
   * has to be fetched individually — a documented N+1 tradeoff until backend ships
   * an aggregation endpoint.
   */
  getSubmissionsForStats: async (formId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: submissionsMockState.filter((s) => s.formId === formId) });
    }
    const list = await httpClient.get<PagedResult<Submission>>(
      `${API_V1}/forms/${formId}/submissions`,
      { params: { Page: 1, PageSize: 200 }, signal },
    );
    const items = list.data.items ?? [];
    const details = await Promise.all(
      items.map((item) =>
        httpClient
          .get<SubmissionDetail>(`${API_V1}/forms/${formId}/submissions/${item.id}`, { signal })
          .then((r) => r.data),
      ),
    );
    return { data: details };
  },

  createTaskFromSubmission: async (
    formId: string,
    submissionId: string,
    data: CreateTaskFromSubmissionPayload,
  ) => {
    if (USE_MOCK) {
      const idx = submissionsMockState.findIndex(
        (s) => s.formId === formId && s.id === submissionId,
      );
      if (idx !== -1)
        submissionsMockState[idx] = { ...submissionsMockState[idx], taskId: `task-${Date.now()}` };
      return Promise.resolve({ data: { eventId: String(Date.now()) } satisfies EventIdResponse });
    }
    return httpClient.post<Record<string, unknown>, EventIdResponse>(
      `${API_V1}/forms/${formId}/submissions/${submissionId}/create-task`,
      {
        projectId: Number(data.projectId),
        boardId: Number(data.boardId),
        statusId: Number(data.statusId),
        groupId: data.groupId ? Number(data.groupId) : null,
        assigneeId: data.assigneeId ? Number(data.assigneeId) : null,
      },
    );
  },

  exportSubmissionsCsv: async (formId: string) => {
    if (USE_MOCK) {
      const header = 'id,submittedByEmail,isAnonymous,taskId,createdAt\n';
      const rows = submissionsMockState
        .filter((s) => s.formId === formId)
        .map(
          (s) =>
            `${s.id},${s.submittedByEmail ?? ''},${s.isAnonymous},${s.taskId ?? ''},${s.createdAt}`,
        )
        .join('\n');
      return Promise.resolve({ data: new Blob([header + rows], { type: 'text/csv' }) });
    }
    return httpClient.get<Blob>(`${API_V1}/forms/${formId}/submissions/export`, {
      responseType: 'blob',
    });
  },
};
